import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import type { BudgetView, InventorySummary, Piece, SetBudget } from '@miraki/contracts';
import {
  useAudit, useRetailer, useRetailerUsers, useRetailerAddresses,
  useAddRetailerAddress, useUpdateRetailerAddress, useRemoveRetailerAddress,
  useSetRetailerCompany, useSetRetailerStatus, useSetTerms, useAssignableRoles,
} from '../api/hooks';
import { api, ApiError } from '../api/client';
import {
  amountProblem, belowInUse, componentsFor, plainAmount, reasonOk, REASON_MAX, standing,
  tolerancePctFor, tolerancePctProblem, toWire, type Tolerance,
} from './retailer-budget';
import { useSession } from '../auth/session';
import { NewRetailerUser } from './Access';
import { AddressRequestsPanel } from './AddressRequestsPanel';
import { KycEvidence } from './KycEvidence';
import { KycDecision } from './KycDecision';
import { mulMoney } from './shop/line-money';
/* D75 — the ONE due-rule formatter, shared with R-18, R-22 and A-40. Never a sentence per screen. */
import { dueRuleSourceWords, dueRuleWords } from './payables-view';
import {
  Alert, Button, Chip, Field, Modal, Money, Note, PageHeader, Panel, Rail, Spinner, Table, Tabs, Tiles, type Tone,
} from '../ui';
import type { RetailerAddress, RetailerDetail as RetailerDetailData, RetailerStatus } from '../api/types';
import { invalidateAll } from '../api/write-keys';

const TONE: Record<RetailerStatus, Tone> = {
  ACTIVE: 'done', ON_HOLD: 'stop', DORMANT: 'lapse', WIND_DOWN: 'run', CLOSED: 'them',
};

/** A-04 and A-05 — record detail, with the trading terms form alongside. */
export function RetailerDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { can } = useSession();
  const { data: r, isLoading, error } = useRetailer(id);
  const { data: audit } = useAudit({ objectId: id, limit: 10 });
  const setTerms = useSetTerms();
  const setStatus = useSetRetailerStatus();

  const [form, setForm] = useState<Record<string, string> | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  /*
   * Which tab is open, in the URL rather than in state alone, so a link to an account's
   * addresses is a link somebody can send. The prototype did the same with `#/retailer/:id/
   * :tab`, and it matters more here than it looks: support pastes these to each other.
   */
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'overview';
  const goTab = (id: string) => setParams(id === 'overview' ? {} : { tab: id }, { replace: true });

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!r) return null;

  const terms = form ?? {
    /*
     * D75 — BLANK means "no term of its own", not 30.
     *
     * `?? 30` filled the box with the old column default, so opening this screen and pressing
     * Save asserted net 30 against an account nobody had decided for — and now that the term
     * decides a due date, that assertion would move the account off the portal's rule. An empty
     * string is the only honest rendering of null, and the input's placeholder says what empty
     * will do.
     */
    paymentTermsDays: r.terms?.paymentTermsDays === null || r.terms?.paymentTermsDays === undefined
      ? '' : String(r.terms.paymentTermsDays),
    advancePct: r.terms?.advancePct ?? '0.0000',
    creditLimit: r.terms?.creditLimit ?? '0.0000',
    incoterm: r.terms?.incoterm ?? 'DAP',
    accountManager: r.terms?.accountManager ?? '',
  };
  const set = (k: string, v: string) => setForm({ ...terms, [k]: v });

  /*
   * D75 — the rule in force, where it came from, and what blank would fall back to. Derived once
   * from the one formatter both this screen and the retailer's own pages read.
   */
  const inForce = dueRuleWords(r.dueRule);
  const inForceFrom = dueRuleSourceWords(r.dueRule);
  const portalPhrase = dueRuleWords(r.portalDueRule, 'phrase');

  async function run<T>(fn: () => Promise<T>) {
    setFailed(null);
    try { await fn(); setForm(null); }
    catch (e) { setFailed(e instanceof ApiError ? e.message : 'That did not work'); }
  }

  /*
   * TABS, not one long scroll.
   *
   * This page had seven panels and is about to carry addresses, company details, KYC
   * documents and change requests as well. Stacked, the things somebody opens the screen
   * FOR — what is owed, what standing the account is in — end up below a fold that grows
   * every time the account record does.
   *
   * The split is by question, not by table: Overview answers "where does this account
   * stand", Details answers "who are they and where do we send things", Trading answers
   * "on what terms", Users answers "who can sign in", Activity answers "what happened".
   * A tab that is empty for an account is still shown, because its absence would read as
   * "this account has none of that", which is a different claim from "nothing here yet".
   */
  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'details', label: 'Details' },
    { id: 'trading', label: 'Trading' },
    { id: 'users', label: 'Users' },
    { id: 'activity', label: 'Activity' },
  ] as const;

  return (
    <>
      {/* A detail screen reached from a list needs a way back to it. Browser back
          works, but a person who arrived from a link, a bookmark or a pasted URL
          has no history to go back through. */}
      <div className="mk-backlink">
        <Link to="/retailers">&larr; All retailers</Link>
      </div>
      <PageHeader
        title={r.name}
        // Absent data reads as absent, not as an em-dash salad. "—, AU · ABN —"
        // looks like a rendering fault; "AU · no ABN recorded" looks like a fact.
        sub={
          <>
            {[r.city, r.countryCode].filter(Boolean).join(', ')}
            {' · '}
            {r.registration.value
              ? `${r.registration.label} ${r.registration.value}`
              : `No ${r.registration.label} recorded`}
          </>
        }
        actions={<Chip tone={TONE[r.status]}>{r.status.replace('_', ' ')}</Chip>}
      />
      {failed ? <Alert tone="stop">{failed}</Alert> : null}
      {/* A request is waiting on a person: shown above the tabs so it is seen whichever tab is open. */}
      <AddressRequestsPanel retailerId={r.id} canDecide={can('retailer.edit')} show="PENDING" />
      {/* Badges are counts, not decoration: a tab worth opening says so before it is opened. */}
      <Tabs tabs={TABS.map((t) => ({ ...t }))} active={tab} onChange={goTab} />

      {tab === 'overview' ? (
        <>
        {r.status === 'CLOSED' ? (
          <Alert tone="run">
            <b>Closed {r.closedAt?.slice(0, 10)}.</b> {r.closureReason}
            {' '}Nothing was deleted — the ledger and the audit log stay (AU-05).
          </Alert>
        ) : null}
  
        <div className="mk-grid2">
          <Panel title="Position" pad>
            <Rail items={[
              // FN-02, per currency (AN-08). A credit shows as a negative, which is money the
              // retailer is owed and must not be clamped to zero (FN-08).
              ['Payable', r.payables.length === 0
                ? <span className="mk-dim">Nothing owed</span>
                : <>{r.payables.map((p) => (
                  <div key={p.currency}><Money amount={p.amount} currency={p.currency} /></div>
                ))}</>],
              ['Tax regime', r.taxRegime],
              ['Trading currency', r.tradingCurrency],
              ['Onboarded from', r.onboardedFrom ? `${r.onboardedFrom.reference} · ${r.onboardedFrom.submittedAt.slice(0, 10)}` : '—'],
            ]} />
            <Note style={{ marginTop: 14 }}>
              The balance is <b>derived from the ledger</b> on every read and never stored (FN-03).
              The registration label comes from the {r.countryCode} country pack, not from this screen.
            </Note>
          </Panel>
        </div>
  
        <BudgetPanel retailerId={r.id} closed={r.status === 'CLOSED'} />
  
        <HeldStockPanel retailerId={r.id} />
        </>
      ) : null}

      {tab === 'details' ? (
        <>
          <CompanyPanel retailer={r} />
          <KycPanel retailer={r} canDownload={can('retailer.approve')} canDecide={can('retailer.approve')} />
          <AddressRequestsPanel retailerId={r.id} canDecide={can('retailer.edit')} show="DECIDED" />
          <AddressesPanel retailerId={r.id} countryCode={r.countryCode} />
        </>
      ) : null}

      {tab === 'trading' ? (
        <>
        {/*
          * PAYMENT STAGES ARE GONE — retired at v1.6, the same as on A-02.
          *
          * "Retired: ... advance-before-manufacturing, staged payment terms" (BRD §1.6), and
          * D31/D34/F4 — "No negotiation, no advance, no payment before dispatch." Under
          * consignment Miraki places the pieces and only SOLD ones become payable, so there is
          * no manufacturing milestone to pay against and nothing for M1-M4 to describe.
          *
          * It was worse than merely stale here. With no terms set the panel announced a
          * DEFAULT schedule — "the standard 30 / 30 / 30 / 10 applies" — stating a commercial
          * arrangement this business does not operate, against an account that never agreed to
          * it. A screen inventing terms is worse than one showing none.
          *
          * What survives is FN-11, net days from invoice, in the Trading terms panel below —
          * which was on Overview and belongs here, on the tab named for it.
          */}
  
          <Panel
            title="Trading terms"
            actions={can('trading_terms.edit') && r.status !== 'CLOSED' ? (
              <Button size="sm" disabled={setTerms.isPending || !form}
                onClick={() => void run(() => setTerms.mutateAsync({
                  id: r.id,
                  body: {
                    /*
                     * D75 — blank sends NULL: "no term of its own, use the portal's rule".
                     *
                     * `Number('')` is 0, and 0 is a real and different term — payable
                     * immediately — so an empty box must not be allowed to arrive as one. Nor
                     * may it arrive as 30: this row is replaced wholesale, so every value here
                     * is an assertion, and asserting a term nobody chose is the defect.
                     */
                    paymentTermsDays: (terms.paymentTermsDays ?? '').trim() === ''
                      ? null : Number(terms.paymentTermsDays),
                    advancePct: terms.advancePct,
                    creditLimit: terms.creditLimit,
                    incoterm: terms.incoterm,
                    accountManager: terms.accountManager || null,
                  },
                }))}>
                Save
              </Button>
            ) : null}
            pad
          >
            {/*
              * D75 — the rule in force, stated above the box that sets it.
              *
              * A number in a box cannot tell Admin what this account's invoices will be dated
              * by: the box may be empty, and empty means the portal's rule, whose SHAPE may be
              * month-end rather than net-days. So the sentence comes from the same
              * `dueRuleWords()` the retailer reads, and the source line answers the question the
              * box cannot — "was this account given a term, or has nobody decided".
              */}
            <Note>
              <b>{t(inForce.key, inForce.values)}</b> {t(inForceFrom.key)}
            </Note>
            <div className="mk-fset">
              <Field
                label="Payment terms (net days)"
                hint={`Leave blank to use this portal's rule: ${t(portalPhrase.key, portalPhrase.values)}. Enter 0 for payable immediately.`}
              >
                <input value={terms.paymentTermsDays} inputMode="numeric"
                  placeholder="Portal's rule"
                  disabled={!can('trading_terms.edit') || r.status === 'CLOSED'}
                  onChange={(e) => set('paymentTermsDays', e.target.value)} />
              </Field>
              {/* Advance %, Credit limit and Incoterm were RETIRED at v1.6 and are no
                  longer editable here: there is no advance under consignment (D33), the
                  inventory budget replaced the credit limit (D37), and the Incoterm went
                  with the old shipment terms. They are still SENT on save, carrying
                  whatever the record already holds, so the request shape and the
                  `trading_terms.set` contract are untouched — this removes an input that
                  offered a decision the model no longer has, not a column.
  
                  What belongs here instead is retailer group, gold pricing tier,
                  inventory budget and subscription (ON-05, ON-08, BG-01, SB-01). None of
                  it exists on this screen yet; see docs/plans/screen-field-audit. */}
              <Field label="Account manager">
                <input value={terms.accountManager}
                  disabled={!can('trading_terms.edit') || r.status === 'CLOSED'}
                  onChange={(e) => set('accountManager', e.target.value)} />
              </Field>
            </div>
          </Panel>
        {can('retailer.hold') && r.status !== 'CLOSED' && r.status !== 'WIND_DOWN' ? (
          <Panel title="Standing" pad>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {(['ACTIVE', 'ON_HOLD', 'DORMANT'] as const).map((s) => (
                <Button key={s} size="sm" tone={r.status === s ? 'solid' : 'ghost'}
                  disabled={r.status === s || setStatus.isPending}
                  onClick={() => void run(() => setStatus.mutateAsync({ id: r.id, body: { status: s } }))}>
                  {s.replace('_', ' ')}
                </Button>
              ))}
            </div>
          </Panel>
        ) : null}
  
        {/*
          * AL-01 — the closing position lives on A-27 now.
          *
          * The panel that was here called a v1.5 route which refused closure on ONE thing: a
          * ledger balance from a third version of the payable formula. That version added
          * SALE_REVERSAL instead of subtracting it, counted retired INVOICE rows as debt, and
          * never looked at mki_pieces — so it would close an account that was still holding
          * Miraki's goods. Both the route and that formula are gone.
          */}
        {can('retailer.close') ? (
          <Panel title="Wind-down" pad>
            <Link to={`/wind-down?retailer=${r.id}`}>
              {r.status === 'CLOSED' ? 'Closing record' : 'Closing position and wind-down'}
            </Link>
          </Panel>
        ) : null}
  
        </>
      ) : null}

      {tab === 'users' ? (
        <RetailerUsersPanel retailerId={r.id} retailerName={r.name} />
      ) : null}

      {tab === 'activity' ? (
        <>
        {audit?.length ? (
          <Panel title="Recent activity">
            <Table
              rows={audit}
              rowKey={(a) => `${a.at}-${a.action}`}
              columns={[
                { header: 'When', render: (a) => <span className="mk-dim">{a.at.replace('T', ' ').slice(0, 16)}</span> },
                { header: 'Actor', render: (a) => a.actor },
                { header: 'Action', render: (a) => <code>{a.action}</code> },
                { header: 'Reason', render: (a) => <span className="mk-dim">{a.reason ?? '—'}</span> },
              ]}
            />
          </Panel>
        ) : null}
        </>
      ) : null}

    </>
  );
}

/**
 * A-04's budget panel — BG-07, BG-08, BG-10.
 *
 * Every figure is the resolver's (`GET /retailers/:id/budget`). The screen compares figures it
 * was given (`retailer-budget.ts`) and never subtracts or adds one: a second statement of BG-03 in
 * the browser is how a budget comes to read differently on two screens.
 *
 * Shown only where the portal offers consignment (the route is capability-gated, and a 404 there
 * would be "not offered", not a fault). The control is behind `budget.edit` and is hidden, not
 * shown-and-refused, without it.
 */
/**
 * BG-04 — what `GET /retailers/:id/budget` returns to Admin: the retailer's `BudgetView`, plus
 * the tolerance band in force, where it came from, and the ceiling the two produce together.
 *
 * Deliberately NOT part of `BudgetView` in `packages/contracts`: that shape is what a retailer
 * reads, and the band is not shown to them (`ConsignmentService.orderingLimit` says why).
 */
type AdminBudgetView = BudgetView & { tolerance: Tolerance; ceiling: string };

function BudgetPanel({ retailerId, closed }: { retailerId: string; closed: boolean }) {
  const { t } = useTranslation();
  const { can, offers, portal } = useSession();
  const qc = useQueryClient();
  const key = ['portal', portal?.code ?? null, 'retailer-budget', retailerId];
  const enabled = offers('memo') && can('retailer.view');
  /*
   * BG-04 — the admin shape: the budget, plus the tolerance band in force and where it came
   * from. `ceiling` is what checkout actually measures an order against. The retailer's own
   * views get none of this (see `ConsignmentService.orderingLimit`); Admin sets it, so Admin
   * sees it.
   */
  const { data: b, isLoading, error } = useQuery({
    queryKey: key,
    enabled,
    queryFn: () => api.get<AdminBudgetView>(`/retailers/${retailerId}/budget`),
  });
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState('');
  const [tolerance, setTolerance] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  if (!enabled) return null;
  if (isLoading) return <Panel title={t('retailerBudget.title')} pad><Spinner /></Panel>;
  if (error) return <Panel title={t('retailerBudget.title')} pad><Alert tone="stop">{error.message}</Alert></Panel>;
  if (!b) return null;

  const state = standing(b);
  const mayEdit = can('budget.edit') && !closed;
  const problem = amountProblem(amount);
  const tolProblem = tolerancePctProblem(tolerance);
  // Read out here, where `b` is narrowed: `save()` is a hoisted declaration and does not inherit
  // the `if (!b) return null` above it.
  const inForce = b.tolerance;
  const showForm = mayEdit && (editing || state === 'NONE');

  async function save() {
    setBusy(true);
    setFailure(null);
    try {
      /*
       * BG-04 — the tolerance is sent only when this edit changed it. Three states, and the
       * difference matters: absent leaves it alone, `null` clears the account's own figure so
       * it takes the portal's again, and a value — '0' included — sets one for this account.
       * `tolerancePctFor` is the only place that decision is made.
       */
      const tolerancePct = tolerancePctFor(tolerance, inForce);
      const body: SetBudget = {
        amount: toWire(amount),
        reason: reason.trim(),
        ...(tolerancePct === undefined ? {} : { tolerancePct }),
      };
      // The answer is the resolver's fresh figure, including the new available budget.
      const next = await api.put<AdminBudgetView>(`/retailers/${retailerId}/budget`, body);
      qc.setQueryData(key, next);
      setEditing(false);
      setAmount('');
      setTolerance('');
      setReason('');
      // BG-10 — the change is in the audit trail; refresh "Recent activity".
      await invalidateAll(qc, [['portal', portal?.code ?? null, 'audit']]);
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : t('retailerBudget.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title={t('retailerBudget.title')}
      pad
      actions={mayEdit && !showForm ? (
        <Button
          size="sm"
          tone="ghost"
          onClick={() => {
            setEditing(true);
            setAmount(plainAmount(b.assigned));
            /*
             * Seeded only where the account has its OWN figure. A retailer on the portal's
             * tolerance opens with an empty box whose hint names the portal's figure, so
             * saving without touching it cannot accidentally promote an inherited 5% into an
             * override — which is the same mistake `mergeStored` prevents on A-36.
             */
            setTolerance(b.tolerance.source === 'RETAILER' ? plainAmount(b.tolerance.pct) : '');
          }}
        >{t('retailerBudget.change')}</Button>
      ) : null}
    >
      {state === 'NONE' ? (
        <Alert tone="run">
          <b>{t('retailerBudget.noneTitle')}</b> {mayEdit ? t('retailerBudget.noneBodyEdit') : t('retailerBudget.noneBody')}
        </Alert>
      ) : null}
      {state === 'OVER' ? <Alert tone="stop">{t('retailerBudget.over')}</Alert> : null}

      <Tiles items={[
        { k: t('retailerBudget.assigned'), v: <Money amount={b.assigned} currency={b.currency} />, s: t('retailerBudget.assignedHint') },
        /*
         * MAX LIMIT — twice the min limit, worked out on every render. Renamed from "Total amount"
         * at the client's request (30-Oct-2026), ADMIN SIDE ONLY: the retailer never sees a second
         * figure, so their screens keep saying "Assigned budget".
         *
         * It still governs nothing, and the hint has to keep saying so. Three numbers now sit on
         * this panel and only one of them refuses an order — the ceiling is the min limit's
         * AVAILABLE portion plus its BG-04a tolerance, which is neither of the two figures above.
         * A more authoritative name on a figure that stops nothing is how a reader comes to
         * believe it is a ceiling.
         */
        { k: t('retailerBudget.totalAmount'), v: <Money amount={mulMoney(b.assigned, 2)} currency={b.currency} />, s: t('retailerBudget.totalAmountHint') },
        { k: t('retailerBudget.available'), v: <Money amount={b.available} currency={b.currency} />, s: t('retailerBudget.availableHint') },
        { k: t('retailerBudget.inUse'), v: <Money amount={b.inUse.total} currency={b.currency} /> },
      ]} />

      {/* BG-10 — what makes the figure up, each component separately. */}
      <Table
        rows={componentsFor(b.releaseOn).map((c) => [c, b.inUse[c]] as const)}
        rowKey={([c]) => c}
        columns={[
          { header: t('retailerBudget.component'), render: ([c]) => t(`retailerBudget.components.${c}`) },
          { header: t('retailerBudget.value'), numeric: true, render: ([, v]) => <Money amount={v} currency={b.currency} /> },
        ]}
      />
      {/* BG-06 — the portal's release setting, said in words. Never a portal name (D46). */}
      <Note>{t(`retailerBudget.release.${b.releaseOn}`)}</Note>

      {/*
        * BG-04 — the tolerance band, and the ceiling it produces. Stated whether or not this
        * account has its own figure: the alternative is Admin reading an empty box and
        * concluding the account gets no tolerance, when in fact it gets the portal's.
        */}
      <Note>
        {t(`retailerBudget.tolerance.${b.tolerance.source}`, { pct: plainAmount(b.tolerance.pct) })}{' '}
        <Trans
          i18nKey="retailerBudget.tolerance.ceiling"
          components={{ m: <Money amount={b.ceiling} currency={b.currency} /> }}
        />
      </Note>

      {showForm ? (
        <div className="mk-fset">
          <Field label={t('retailerBudget.newAmount', { currency: b.currency })}
            hint={problem && amount ? t(`retailerBudget.problem.${problem}`) : t('retailerBudget.amountHint')}>
            <input value={amount} inputMode="decimal" onChange={(e) => setAmount(e.target.value)} />
          </Field>
          {/*
            * BG-04 — "make it configurable at retailer level by admin" (client, 29-Oct-2026).
            * Beside the budget because it is read with the budget: on its own, a percentage
            * means nothing. The placeholder carries the portal's figure so the empty box reads
            * as "the portal's", which is what it means, rather than as "none".
            */}
          <Field
            label={t('retailerBudget.tolerance.label')}
            hint={tolProblem
              ? t(`retailerBudget.tolerance.problem.${tolProblem}`)
              : t('retailerBudget.tolerance.hint', { pct: plainAmount(b.tolerance.pct) })}
          >
            <input
              value={tolerance}
              inputMode="decimal"
              placeholder={b.tolerance.source === 'RETAILER' ? undefined : plainAmount(b.tolerance.pct)}
              onChange={(e) => setTolerance(e.target.value)}
            />
          </Field>
          <Field label={t('retailerBudget.reason')} hint={t('retailerBudget.reasonHint')}>
            <input value={reason} maxLength={REASON_MAX} onChange={(e) => setReason(e.target.value)} />
          </Field>
          {/* BG-08 — warn, never block: this is how Admin reduces an account that is over. */}
          {belowInUse(amount, b) ? <Alert tone="run">{t('retailerBudget.belowInUse')}</Alert> : null}
          {failure ? <Alert tone="stop">{failure}</Alert> : null}
          <p>
            <Button size="sm" disabled={busy || problem !== null || tolProblem !== null || !reasonOk(reason)} onClick={() => void save()}>
              {state === 'NONE' ? t('retailerBudget.set') : t('retailerBudget.save')}
            </Button>{' '}
            {state !== 'NONE' ? (
              <Button size="sm" tone="ghost" onClick={() => { setEditing(false); setTolerance(''); setFailure(null); }}>{t('retailerBudget.cancel')}</Button>
            ) : null}
          </p>
        </div>
      ) : null}
      <p className="mk-hint">{t('retailerBudget.traceable')}</p>
    </Panel>
  );
}

/**
 * CN-08, AD-03 — what this retailer holds, as Admin sees it: "the same figures, plus ageing".
 *
 * The same `/retailers/:id/inventory` the retailer's own R-20 reads, through the same
 * `inventory()`, so Admin looking at an account and the account looking at itself cannot
 * disagree about which pieces are out. Nothing is computed here: the summary and `daysHeld`
 * arrive from the API, and `daysHeld` ends at the sale for a sold piece (D80).
 *
 * A compact panel rather than a copy of R-20: Admin is checking a position, not working the
 * inventory. The ageing is what CN-08 adds over the retailer's own view, so it leads — oldest
 * first, because a piece held a long time is the one worth asking about.
 */
function HeldStockPanel({ retailerId }: { retailerId: string }) {
  const { t } = useTranslation();
  const { can, offers, portal } = useSession();
  const enabled = offers('memo') && can('retailer.view');
  const { data, isLoading, error } = useQuery({
    queryKey: ['portal', portal?.code ?? null, 'retailer-inventory', retailerId],
    enabled,
    queryFn: () => api.get<{ summary: InventorySummary; pieces: Piece[] }>(`/retailers/${retailerId}/inventory`),
  });

  if (!enabled) return null;
  const title = t('retailerStock.title');
  if (isLoading) return <Panel title={title} pad><Spinner /></Panel>;
  /*
   * A 501 NOT_YET is not an error here; it would be if the consignment service were still a
   * stub. It is not any more, so anything that arrives is a real failure and is shown as one.
   */
  if (error) return <Panel title={title} pad><Alert tone="stop">{error.message}</Alert></Panel>;
  if (!data) return null;

  const held = [...data.pieces]
    .filter((p) => p.status === 'HELD')
    .sort((a, b) => (b.daysHeld ?? 0) - (a.daysHeld ?? 0));

  return (
    <Panel title={title} pad>
      <Tiles
        items={[
          { k: t('retailerStock.onHand'), v: String(data.summary.piecesOnHand) },
          { k: t('retailerStock.wholesale'), v: <Money amount={data.summary.wholesaleValue} currency={data.summary.currency} /> },
          { k: t('retailerStock.rp'), v: <Money amount={data.summary.rpValue} currency={data.summary.currency} /> },
        ]}
      />
      {held.length === 0 ? (
        <Note>{t('retailerStock.none')}</Note>
      ) : (
        <Table
          rows={held}
          rowKey={(p) => p.id}
          columns={[
            { header: t('retailerStock.colPiece'), render: (p) => <>{p.description}<div className="mk-hint">{p.jobNumber}</div></> },
            { header: t('retailerStock.colWholesale'), numeric: true, render: (p) => <Money amount={p.wholesaleValue} currency={p.currency} /> },
            { header: t('retailerStock.colDays'), numeric: true, render: (p) => (p.daysHeld === null ? '—' : t('retailerStock.days', { n: p.daysHeld })) },
          ]}
        />
      )}
      {data.summary.byArticle.length > 0 ? (
        <p className="mk-dim">
          {data.summary.byArticle.map((a) => `${a.article}: ${a.pieces}`).join(' · ')}
        </p>
      ) : null}
    </Panel>
  );
}


/**
 * Who can sign in to this account (RL-11), on the account itself.
 *
 * A-06 answers "find me this person"; this answers "who is on this account", and those are
 * different questions asked at different moments. Somebody looking at a retailer because of a
 * budget query or a late payment is already here — making them leave for a separate screen
 * and filter it back down to this account is the kind of thing that stops getting done.
 *
 * Read-only on purpose. Editing lives in A-06, which has the dialogs, the refusals and the
 * audit wiring; a second set here would be a second place for those rules to be slightly
 * wrong. The names link there, carrying the account with them.
 *
 * Shown only to somebody holding `retailer_user.manage`. For anyone else the panel is absent
 * rather than empty: an empty "Users" panel says this account has no logins, which is a
 * different and alarming claim.
 */
function RetailerUsersPanel({ retailerId, retailerName }: { retailerId: string; retailerName: string }) {
  const { can } = useSession();
  const mayManage = can('retailer_user.manage');
  const { data, isLoading } = useRetailerUsers(mayManage, retailerId);
  const { data: roles } = useAssignableRoles();
  const [adding, setAdding] = useState(false);

  if (!mayManage) return null;
  if (isLoading) return <Panel title="Users" pad><Spinner /></Panel>;

  const users = data ?? [];

  /*
   * An account with no active Owner is worth saying out loud. RL-13 gives invitation,
   * re-roling and reactivation to the Owner alone, so an account without one cannot manage
   * its own people — and the only way back is somebody here noticing. That is exactly the
   * kind of thing nobody goes looking for, so it is stated where the users are.
   */
  const hasOwner = users.some((u) => u.isActive && u.roles.some((r) => r.code === 'ret_owner'));
  const active = users.filter((u) => u.isActive).length;

  // RL-12 — the retailer's own three. Internal roles live in the same table and the API
  // refuses them here, so they are never offered.
  const assignable = (roles ?? []).filter((x) => x.audience === 'RETAILER' && x.isActive);

  return (
    <Panel
      title="Users"
      actions={mayManage ? (
        <Button size="sm" onClick={() => setAdding(true)}>Add user</Button>
      ) : null}
    >
      {users.length === 0 ? (
        <Note>
          <b>{retailerName} has no logins.</b> Nobody at this account can sign in. Create one
          from <Link to="/users">Users</Link>.
        </Note>
      ) : null}

      {users.length > 0 && !hasOwner ? (
        <Note>
          <b>No active Owner.</b> Nobody at this account can invite, re-role or reactivate its
          own users (RL-13). Give the Owner role to one of them from <Link to="/users">Users</Link>.
        </Note>
      ) : null}

      {users.length > 0 ? (
        <Table
          rows={users}
          rowKey={(u) => u.id}
          columns={[
            {
              header: 'Name',
              render: (u) => (
                <>
                  {/* Straight to THIS person's editor. It used to land on the Users list,
                      which is the right screen and the wrong place on it — you arrived
                      knowing exactly who you meant and had to find them again. */}
                  <Link to={`/users?user=${u.id}`}>{u.name}</Link>
                  {u.isPrimary ? <> <Chip tone="run">Primary</Chip></> : null}
                </>
              ),
            },
            { header: 'Email', render: (u) => <span className="mk-dim">{u.email}</span> },
            {
              header: 'Roles',
              render: (u) => u.roles.map((r) => r.name).join(', ')
                || <span className="mk-dim">none</span>,
            },
            {
              header: 'Last signed in',
              render: (u) => (u.lastSignInAt
                ? <span className="mk-dim">{u.lastSignInAt.replace('T', ' ').slice(0, 16)}</span>
                // "Never" is a fact worth reading: a login created and never used is usually
                // one whose password never reached the person it was made for.
                : <span className="mk-dim">Never</span>),
            },
            {
              header: 'State',
              render: (u) => (
                <Chip tone={u.isActive ? 'done' : 'stop'}>
                  {u.isActive ? 'Active' : 'Suspended'}
                </Chip>
              ),
            },
          ]}
        />
      ) : null}

      {users.length > 0 ? (
        <p className="mk-hidden-rows">
          {active} of {users.length} can sign in.
        </p>
      ) : null}

      {/* The same dialog A-06 uses, with the account already decided — one create path, so
          its refusals cannot differ depending on where you started from. */}
      {adding ? (
        <NewRetailerUser
          roles={assignable}
          fixedRetailerId={retailerId}
          fixedRetailerName={retailerName}
          onDone={() => setAdding(false)}
        />
      ) : null}
    </Panel>
  );
}


/** What each kind is for, in the words a person would use. Order is the order they appear. */
const ADDRESS_KINDS = [
  { kind: 'REGISTERED' as const, label: 'Registered', hint: 'The address on the business registration. Used on tax documents.' },
  { kind: 'BILLING' as const, label: 'Billing', hint: 'Where invoices and statements go.' },
  { kind: 'SHIPPING' as const, label: 'Shipping', hint: 'Where consignments are sent. An account may hold several.' },
];

/**
 * ON-08 — the addresses on a retailer record.
 *
 * Grouped by kind rather than listed flat, because the kinds are not three flavours of the
 * same thing: one is a legal fact, one decides where money is asked for, one decides where
 * stock physically goes. A flat list sorted by creation date buries which is which.
 *
 * Every kind is shown even when empty, and says what it is for. An account with no shipping
 * address is a real and important state — nothing can be sent to it — and a panel that
 * simply omitted the heading would make that invisible rather than obvious.
 *
 * Registered and billing are singular and the server refuses a second; the UI stops offering
 * "Add" once one exists rather than letting somebody fill a form that will be refused.
 */
function AddressesPanel({ retailerId, countryCode }: { retailerId: string; countryCode: string }) {
  const { can } = useSession();
  const mayEdit = can('trading_terms.edit');
  const { data, isLoading, error } = useRetailerAddresses(retailerId);
  const add = useAddRetailerAddress(retailerId);
  const update = useUpdateRetailerAddress(retailerId);
  const remove = useRemoveRetailerAddress(retailerId);

  const [editing, setEditing] = useState<{ id?: string; kind: RetailerAddress['kind'] } | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  if (isLoading) return <Panel title="Addresses" pad><Spinner /></Panel>;
  if (error) return <Panel title="Addresses" pad><Alert tone="stop">{error.message}</Alert></Panel>;

  const all = data ?? [];
  const run = async (fn: () => Promise<unknown>) => {
    setFailed(null);
    try { await fn(); setEditing(null); }
    catch (e) { setFailed(e instanceof ApiError ? e.message : 'That did not work'); }
  };

  return (
    <Panel title="Addresses">
      {failed ? <Alert tone="stop">{failed}</Alert> : null}
      {all.length === 0 ? (
        <Note>
          <b>This account has no addresses recorded.</b> Nothing can be invoiced or shipped to
          it until at least a billing and a shipping address exist.
        </Note>
      ) : null}

      {ADDRESS_KINDS.map(({ kind, label, hint }) => {
        const ofKind = all.filter((a) => a.kind === kind);
        const singular = kind !== 'SHIPPING';
        return (
          <div key={kind} className="mk-addr-group">
            <h3 className="mk-addr-h">
              {label}
              {mayEdit && !(singular && ofKind.length > 0) ? (
                <Button size="sm" tone="ghost" onClick={() => { setFailed(null); setEditing({ kind }); }}>
                  Add
                </Button>
              ) : null}
            </h3>
            <p className="mk-hint">{hint}</p>

            {ofKind.length === 0 ? (
              <p className="mk-dim">None recorded.</p>
            ) : ofKind.map((a) => (
              <div key={a.id} className="mk-addr">
                <div>
                  {a.line1}{a.line2 ? <>, {a.line2}</> : null}
                  <div className="mk-dim">
                    {[a.city, a.postalCode, a.countryCode].filter(Boolean).join(' · ')}
                  </div>
                  {a.contactName || a.contactPhone ? (
                    <div className="mk-dim">
                      {[a.contactName, a.contactPhone].filter(Boolean).join(' · ')}
                    </div>
                  ) : null}
                </div>
                <div>
                  {/* Only meaningful where several can exist — a lone billing address being
                      "the default" says nothing. */}
                  {a.isDefault && !singular ? <Chip tone="done">Default</Chip> : null}
                  {mayEdit ? (
                    <>
                      {' '}
                      <Button size="sm" tone="ghost" onClick={() => { setFailed(null); setEditing({ id: a.id, kind }); }}>
                        Edit
                      </Button>
                      {' '}
                      <Button
                        size="sm" tone="danger" disabled={remove.isPending}
                        onClick={() => void run(() => remove.mutateAsync({ id: a.id }))}
                      >
                        Remove
                      </Button>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {editing ? (
        <AddressEditor
          key={editing.id ?? `new-${editing.kind}`}
          kind={editing.kind}
          existing={editing.id ? all.find((a) => a.id === editing.id) : undefined}
          defaultCountry={countryCode}
          busy={add.isPending || update.isPending}
          onCancel={() => setEditing(null)}
          onSave={(body) => void run(() => (editing.id
            ? update.mutateAsync({ id: editing.id, body })
            : add.mutateAsync(body)))}
        />
      ) : null}
    </Panel>
  );
}

/**
 * One form for adding and editing, because the fields are identical and two forms would be
 * two places for the country default to drift.
 *
 * `kind` is shown and never editable — the server refuses a change of kind, and an input that
 * collects something the server will reject is a worse way to say "no" than not offering it.
 */
function AddressEditor(
  { kind, existing, defaultCountry, busy, onSave, onCancel }: {
    kind: RetailerAddress['kind'];
    existing: RetailerAddress | undefined;
    defaultCountry: string;
    busy: boolean;
    onSave: (body: Record<string, unknown>) => void;
    onCancel: () => void;
  },
) {
  const [line1, setLine1] = useState(existing?.line1 ?? '');
  const [line2, setLine2] = useState(existing?.line2 ?? '');
  const [city, setCity] = useState(existing?.city ?? '');
  const [postalCode, setPostal] = useState(existing?.postalCode ?? '');
  const [country, setCountry] = useState(existing?.countryCode ?? defaultCountry);
  const [contactName, setContactName] = useState(existing?.contactName ?? '');
  const [contactPhone, setContactPhone] = useState(existing?.contactPhone ?? '');
  const [isDefault, setIsDefault] = useState(existing?.isDefault ?? false);

  const ready = line1.trim().length > 0 && city.trim().length > 0;
  const label = ADDRESS_KINDS.find((k) => k.kind === kind)!.label;

  return (
    <Modal
      title={`${existing ? 'Edit' : 'Add'} ${label.toLowerCase()} address`}
      onClose={onCancel}
      footer={
        <>
          <Button
            disabled={busy || !ready}
            onClick={() => onSave({
              kind, line1, line2: line2 || null, city, postalCode: postalCode || null,
              countryCode: country || null,
              contactName: contactName || null, contactPhone: contactPhone || null,
              isDefault,
            })}
          >
            Save
          </Button>
          <Button tone="ghost" onClick={onCancel}>Cancel</Button>
        </>
      }
    >
      <div className="mk-fset">
        <Field label="Address line 1"><input value={line1} onChange={(e) => setLine1(e.target.value)} /></Field>
        <Field label="Address line 2"><input value={line2} onChange={(e) => setLine2(e.target.value)} /></Field>
        <Field label="City"><input value={city} onChange={(e) => setCity(e.target.value)} /></Field>
        {/* Not required: plenty of jurisdictions have none, and a mandatory field that cannot
            be filled honestly gets filled with a full stop. */}
        <Field label="Postal code" hint="Optional — not every country uses one.">
          <input value={postalCode} onChange={(e) => setPostal(e.target.value)} />
        </Field>
        <Field label="Country" hint="Two-letter code.">
          <input value={country} maxLength={2} onChange={(e) => setCountry(e.target.value.toUpperCase())} />
        </Field>
      </div>

      {kind === 'SHIPPING' ? (
        <div className="mk-fset">
          <Field label="Contact name" hint="Whoever receives a consignment is rarely whoever signs the invoice.">
            <input value={contactName} onChange={(e) => setContactName(e.target.value)} />
          </Field>
          <Field label="Contact phone">
            <input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
          </Field>
          <Field label="Default">
            <label className="mk-check">
              <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
              {' '}Send consignments here unless told otherwise
            </label>
          </Field>
        </div>
      ) : null}
    </Modal>
  );
}


/**
 * ON-08 — the company details and contacts Admin maintains.
 *
 * Read-first, edit on request. This is a reference panel far more often than a form: people
 * open it to find a phone number, not to change one, and a screen of inputs invites a stray
 * keystroke into a legal name.
 *
 * The REASON is required by the server and asked for here rather than invented, because ON-08
 * says every change is audited with one. A reason the UI made up ("edited from the admin
 * console") is worse than none: it fills the audit column with a sentence that answers nothing
 * while looking like it does.
 *
 * Contacts sit beside the company fields and not with Users on purpose. A login is somebody
 * who signs in; a contact is who Miraki rings. The owner who never logs in is exactly the
 * person whose number this is, and filing them under Users would have meant inventing a login
 * for them or losing the number.
 */
/**
 * ON-02a, ON-02, D94 — the KYC section the Details tab did not have.
 *
 * Before this the entirety of visible KYC on an account was the registration ID in the page
 * header. The tab's own comment at the top of this file already said it was "about to carry
 * addresses, company details, KYC documents and change requests"; this is that.
 *
 * **CR-57 P3 / D105 — it now also carries Admin's DECISION on the pack** (`KycDecision`, above the
 * evidence): KYC gates the order, and this is where it is approved or rejected. The evidence itself is
 * still read-only, and now includes anything the retailer filed themselves after signing in.
 *
 * **Read-only, and read through the application.** `mki_retailers.application_id` is the link and
 * the API follows it, so what A-04 shows is the pack the approval was made on rather than a copy
 * taken at approval that could drift from it. Nothing on this screen can add, replace or remove a
 * document: the application is where they live, and once an application is decided its evidence
 * is fixed (a correction is a new application, the same way a ledger correction is a new row).
 *
 * `canDownload` is `retailer.approve`, not `retailer.view` — identity documents must not follow
 * the permission that lets somebody look up a budget. The names, formats and dates DO follow
 * `retailer.view`, which is why this panel renders for everyone who can open the screen: the
 * account record should be able to say what is on file without handing out the files.
 */
function KycPanel(
  { retailer: r, canDownload, canDecide }: { retailer: RetailerDetailData; canDownload: boolean; canDecide: boolean },
) {
  const { t } = useTranslation();
  return (
    <Panel title={t('kyc.title')} pad>
      {/* CR-57 P3 / D105 — the decision, above the evidence it is made on. */}
      <KycDecision retailer={r} canDecide={canDecide} />
      {/*
        * Where the pack came from, said once. An account with no application behind it — seeded,
        * or migrated from before the portal existed — has no pack, and that is a true statement
        * about the account rather than a missing resource.
        */}
      <Note>
        {r.onboardedFrom
          ? t('retailerKyc.from', {
            reference: r.onboardedFrom.reference,
            on: r.onboardedFrom.submittedAt.slice(0, 10),
          })
          : t('retailerKyc.noApplication')}
      </Note>
      <KycEvidence retailerId={r.id} canEdit={false} canDownload={canDownload} />
    </Panel>
  );
}

function CompanyPanel({ retailer: r }: { retailer: RetailerDetailData }) {
  const { can } = useSession();
  const mayEdit = can('retailer.edit');
  const save = useSetRetailerCompany(r.id);

  const [editing, setEditing] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: r.name,
    city: r.city ?? '',
    taxRegistration: r.registration.value ?? '',
    tier: r.tier ?? '',
    contactName: r.contactName ?? '',
    contactEmail: r.contactEmail ?? '',
    contactPhone: r.contactPhone ?? '',
    reason: '',
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const registrationChanged = form.taxRegistration.trim() !== (r.registration.value ?? '');
  const ready = form.name.trim().length >= 2 && form.reason.trim().length >= 3;

  const submit = async () => {
    setFailed(null);
    try {
      await save.mutateAsync(form);
      setEditing(false);
      setForm((f) => ({ ...f, reason: '' }));
    } catch (e) {
      setFailed(e instanceof ApiError ? e.message : 'That did not work');
    }
  };

  const dash = <span className="mk-dim">—</span>;

  return (
    <Panel
      title="Company"
      pad
      actions={mayEdit && r.status !== 'CLOSED' && !editing
        ? <Button size="sm" tone="ghost" onClick={() => { setFailed(null); setEditing(true); }}>Edit</Button>
        : null}
    >
      {failed ? <Alert tone="stop">{failed}</Alert> : null}

      {!editing ? (
        <Rail items={[
          ['Legal name', r.name],
          [r.registration.label, r.registration.value ?? <span className="mk-dim">Not recorded</span>],
          ['City', r.city || dash],
          ['Country', r.countryCode],
          ['Tier', r.tier || dash],
          ['Contact', r.contactName || dash],
          ['Contact email', r.contactEmail || dash],
          ['Contact phone', r.contactPhone || dash],
        ]} />
      ) : (
        <>
          <div className="mk-fset">
            <Field label="Legal name"><input value={form.name} onChange={set('name')} /></Field>
            <Field
              label={r.registration.label}
              hint="Decides the tax treatment on every invoice from here on."
            >
              <input value={form.taxRegistration} onChange={set('taxRegistration')} />
            </Field>
            <Field label="City"><input value={form.city} onChange={set('city')} /></Field>
            <Field label="Tier" hint="Commercial banding. A label — it gates nothing.">
              <input value={form.tier} onChange={set('tier')} />
            </Field>
          </div>

          <div className="mk-fset">
            <Field label="Contact name" hint="Who Miraki calls. Not necessarily someone with a login.">
              <input value={form.contactName} onChange={set('contactName')} />
            </Field>
            <Field label="Contact email">
              <input type="email" value={form.contactEmail} onChange={set('contactEmail')} />
            </Field>
            <Field label="Contact phone">
              <input value={form.contactPhone} onChange={set('contactPhone')} />
            </Field>
          </div>

          {/* Stated at the moment it becomes true, not as a permanent warning nobody reads. */}
          {registrationChanged ? (
            <Alert tone="us">
              <b>You are changing the {r.registration.label.toLowerCase()}.</b> It decides the tax
              treatment of every invoice issued from here on — documents already sent are
              unaffected.
            </Alert>
          ) : null}

          <div className="mk-fset">
            <Field label="Reason" hint="Required. ON-08 — every change to this record is audited with one.">
              <input
                value={form.reason}
                onChange={set('reason')}
                placeholder="e.g. Corrected after the retailer's own notification"
              />
            </Field>
          </div>

          <div style={{ display: 'flex', gap: 'var(--mk-space-2)' }}>
            <Button disabled={!ready || save.isPending} onClick={() => void submit()}>Save</Button>
            <Button tone="ghost" onClick={() => { setEditing(false); setFailed(null); }}>Cancel</Button>
            {!ready && form.name.trim().length >= 2
              ? <span className="mk-hint">A reason is needed before this can be saved.</span>
              : null}
          </div>
        </>
      )}
    </Panel>
  );
}
